const keyOf = (el) => el?.dataset?.productHandle || '';

class ProductCard extends HTMLElement {
  constructor() {
    super();
    this._ctrl = null;
    this._fetching = false;
  }

  showHover() {
    if (window.innerWidth < 1024 || !this.hoverImageWrapper) return;
    if (this.imageWrapper) this.imageWrapper.style.display = 'none';
    if (this.hoverImageWrapper) this.hoverImageWrapper.style.display = 'block';
    this.container?.classList.add('is-hover');
  }

  hideHover() {
    if (window.innerWidth < 1024 || !this.hoverImageWrapper) return;
    if (this.imageWrapper) this.imageWrapper.style.display = 'block';
    if (this.hoverImageWrapper) this.hoverImageWrapper.style.display = 'none';
    this.container?.classList.remove('is-hover');
  }

  applyTypography(targetRoot = this) {
    const d = this.wrapper?.dataset;
    if (!d) return;
    const classes = [];
    if (d.font) classes.push(`font-${d.font}`);
    if (d.fontSize) classes.push(`size-${d.fontSize}`);
    if (d.fontSizeMobile) classes.push(`small--size-${d.fontSizeMobile}`);
    if (d.fontTitleCase) classes.push(`text-${d.fontTitleCase}`);
    const target = (targetRoot.querySelector?.('[data-typography]')) || targetRoot;
    if (classes.length) target.classList.add(...classes);
  }

  applyInfoHeight() {
    if (window.innerWidth >= 1025) {
      const infoEl = this.querySelector('[data-product-info]');
      if (infoEl) {
        const height = infoEl.offsetHeight;
        infoEl.style.minHeight = `${height}px`;
      }
    }
  }

  reapplySwatchState(freshRoot, { swatchOrder, activeKey, selectedKey }) {
    const wrap = freshRoot.querySelector('.product-card-swatches');
    if (!wrap) return;
    const swatches = [...wrap.querySelectorAll('.product-card-swatch')];
    if (!swatches.length) return;
    wrap.style.display = 'none';
    if (Array.isArray(swatchOrder) && swatchOrder.length) {
      const reordered = swatchOrder.map(h => swatches.find(s => keyOf(s) === h)).filter(Boolean);
      if (reordered.length) reordered.forEach(s => wrap.appendChild(s));
    }
    swatches.forEach(s => s.classList.remove('active', 'selected'));
    if (activeKey) {
      const a = [...wrap.querySelectorAll('.product-card-swatch')].find(s => keyOf(s) === activeKey);
      if (a) a.classList.add('active');
    }
    if (selectedKey) {
      const sel = [...wrap.querySelectorAll('.product-card-swatch')].find(s => keyOf(s) === selectedKey);
      if (sel) sel.classList.add('selected');
    }
    wrap.style.display = '';
  }

  connectedCallback() {
    this.wrapper = this.closest('[data-collection-handle],[data-font],[data-font-size],[data-font-size-mobile],[data-font-title-case]');
    this.container = this.querySelector('.product-card');

    this.swatches = this.querySelectorAll('.product-card-swatch');
    this.imageWrapper = this.querySelector('[data-product-image]');
    this.hoverImageWrapper = this.querySelector('[data-product-hover]');

    this.init();
    this.applyTypography();
    this.applyInfoHeight();
  }

  init() {
    if (!this.container) return;
    const onEnter = () => this.showHover();
    const onLeave = () => this.hideHover();
    this.container.addEventListener('mouseenter', onEnter);
    this.container.addEventListener('mouseleave', onLeave);

    this.swatches.forEach((swatch) => {
      swatch.addEventListener('click', async (e) => {
        e.preventDefault();
        const handle = swatch.dataset.productHandle;
        if (!handle) return;
        const selectedKey = keyOf(swatch);
        const prevActiveEl = this.querySelector('.product-card-swatch.active');
        const activeKey = prevActiveEl ? keyOf(prevActiveEl) : null;
        this.swatches.forEach(s => s.classList.remove('selected'));
        swatch.classList.add('selected');
        const container = this.querySelector('.product-card-swatches') || swatch.parentElement;
        const swatchOrder = container ? [...container.querySelectorAll('.product-card-swatch')].map(s => keyOf(s)) : [];
        await this.updateCard(handle, swatchOrder, activeKey, selectedKey);
      });
    });
  }

  async updateCard(handle, swatchOrder = [], activeKey = null, selectedKey = null) {
    try {
      const wasHover = this.container?.classList?.contains('is-hover');
      if (this._ctrl) this._ctrl.abort();
      this._ctrl = new AbortController();
      this._fetching = true;

      const collectionHandle = this.wrapper?.dataset?.collectionHandle || null;
      const url = collectionHandle
        ? `/collections/${encodeURIComponent(collectionHandle)}/products/${encodeURIComponent(handle)}?section_id=product-card`
        : `/products/${encodeURIComponent(handle)}?section_id=product-card`;

      const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' }, signal: this._ctrl.signal });
      if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);

      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const freshRoot =
        doc.querySelector('product-card') ||
        doc.querySelector('[data-product-card-root]') ||
        doc.querySelector('.product-card')?.closest('product-card');

      if (freshRoot) {
        this.applyTypography(freshRoot);

        this.reapplySwatchState(freshRoot, { swatchOrder, activeKey, selectedKey });

        if (wasHover) {
          const freshContainer = freshRoot.querySelector('.product-card');
          if (freshContainer) freshContainer.classList.add('is-hover');
          const fImg = freshRoot.querySelector('[data-product-image]');
          const fHover = freshRoot.querySelector('[data-product-hover]');
          if (fImg && fHover) {
            fImg.style.display = 'none';
            fHover.style.display = 'block';
          }
        }

        this.replaceWith(freshRoot);

        try {
          const handle = freshRoot.querySelector('.klaviyo-product-container')?.dataset.klaviyoHandle;
          if (handle && window._bisTriggerCache?.[handle]) {
            const clone = window._bisTriggerCache[handle].cloneNode(true);
            const existingContainer = freshRoot.querySelector('.klaviyo-product-container');
            if (existingContainer && existingContainer.parentElement) {
              existingContainer.parentElement.replaceChild(clone, existingContainer);
            }
          } else {
            console.warn(`⚠️ No cached BIS product container found for ${handle}`);
          }
        } catch (err) {
          console.error('Klaviyo BIS product container injection from cache failed', err);
        }

        if (typeof alignProductCardsHeight === 'function') {
          requestAnimationFrame(alignProductCardsHeight);
        }

        return;
      }

      this.outerHTML = html;
    } catch (e) {
      if (e?.name !== 'AbortError') console.error('updateCard fetch/replace error', e);
    }
    finally {
      this._fetching = false;
    }
  }

}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}